import { useTimeline } from '@src/features/absence';
import { useFormContext } from '@src/shared/form';
import { formatIsoDate } from '@src/shared/lib';
import { useEffect, useMemo } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import {
  buildHolidayLookup,
  getAbsencesIdsForRange,
  getUserAbsences,
  userResourceId,
} from '../../../lib';
import type { ScheduleFormValues } from '../Form';
import TotalDatesOrHours from '../TotalDatesOrHours';
import { DatesRangeField } from './Dates';
import HoursToggle from './HoursToggle';
import NotesField from './NotesField';
import TimeRangeField from './Time';
import TypeField from './TypeField';
import UsersField from './User';

type FormFieldsetProps = {
  disabled?: boolean;
  disableUserField?: boolean;
};

function areIdsEqual(left: number[], right: number[]) {
  if (left.length !== right.length) {
    return false;
  }

  return left.every((id, index) => id === right[index]);
}

export default function FormFieldset({
  disabled = false,
  disableUserField = false,
}: FormFieldsetProps) {
  const { setValue, watch } =
    useFormContext() as UseFormReturn<ScheduleFormValues>;
  const {
    absences,
    setSelectedSlot,
    selectedSlotData,
    calendar,
    isDayView,
    setSelectedUserId,
    expandAndScrollToResource,
    holidays,
    users,
  } = useTimeline();
  const holidayLookup = useMemo(() => buildHolidayLookup(holidays), [holidays]);
  const userId = watch('userId');
  const selectedUserBranchId = useMemo(
    () => users.find((user) => user.id === userId)?.branch?.id ?? null,
    [userId, users],
  );
  const [dateFrom, dateTo] = watch(['dateFrom', 'dateTo']);
  const [startTime, endTime] = watch(['startTime', 'endTime']);

  useEffect(() => {
    setSelectedSlot((slot) => {
      if (!slot) return null;
      const nextStartTime = isDayView ? startTime || null : null;
      const nextEndTime = isDayView ? endTime || null : null;
      let selectedIds: number[] = [];
      if (dateFrom && dateTo) {
        selectedIds = getAbsencesIdsForRange(
          dateFrom,
          dateTo,
          getUserAbsences(slot?.userId, absences),
          {
            dayView: isDayView,
            startTime: nextStartTime,
            endTime: nextEndTime,
            excludeIds: slot.editedAbsenceIds ?? (slot.editedAbsenceId ? [slot.editedAbsenceId] : []),
          },
        );
      } else if (slot.start && slot.end) {
        selectedIds = getAbsencesIdsForRange(
          new Date(slot.start),
          new Date(slot.end),
          getUserAbsences(slot?.userId, absences),
          {
            dayView: isDayView,
            startTime: nextStartTime,
            endTime: nextEndTime,
            excludeIds: slot.editedAbsenceIds ?? (slot.editedAbsenceId ? [slot.editedAbsenceId] : []),
          },
        );
      }
      const changedSlot = {
        userId,
        resourceId: userResourceId(userId),
        ...(dateFrom ? { start: formatIsoDate(dateFrom) } : {}),
        ...(dateTo ? { end: formatIsoDate(dateTo) } : {}),
        startTime: nextStartTime,
        endTime: nextEndTime,
      };
      const nextSlot = {
        ...slot,
        ...changedSlot,
        selectedIds,
      };

      if (
        slot.userId === nextSlot.userId &&
        slot.resourceId === nextSlot.resourceId &&
        slot.start === nextSlot.start &&
        slot.end === nextSlot.end &&
        slot.startTime === nextSlot.startTime &&
        slot.endTime === nextSlot.endTime &&
        slot.editedAbsenceId === nextSlot.editedAbsenceId &&
        areIdsEqual(slot.selectedIds, nextSlot.selectedIds)
      ) {
        return slot;
      }

      return nextSlot;
    });
  }, [
    absences,
    userId,
    dateFrom,
    dateTo,
    endTime,
    isDayView,
    setSelectedSlot,
    startTime,
  ]);

  useEffect(() => {
    setSelectedUserId(userId ?? null);
    if (userId) {
      // Delay scroll to run after selection sync updates
      // the calendar layout.
      const timer = setTimeout(() => {
        expandAndScrollToResource(userId);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [userId, setSelectedUserId, expandAndScrollToResource]);

  useEffect(() => {
    if (selectedSlotData?.start) {
      setValue('dateFrom', new Date(selectedSlotData.start));
    }
    if (selectedSlotData?.end) {
      setValue('dateTo', new Date(selectedSlotData.end));
    }
    setValue('startTime', selectedSlotData?.startTime ?? null);
    setValue('endTime', selectedSlotData?.endTime ?? null);
    setValue('userId', selectedSlotData?.userId ?? null);
    setValue('typeId', selectedSlotData?.typeId ?? null);
    setValue('tentative', selectedSlotData?.tentative ?? false);
    setValue('notes', selectedSlotData?.notes ?? '');
  }, [
    setValue,
    selectedSlotData?.userId,
    selectedSlotData?.start,
    selectedSlotData?.end,
    selectedSlotData?.startTime,
    selectedSlotData?.endTime,
    selectedSlotData?.notes,
    selectedSlotData?.tentative,
    selectedSlotData?.typeId,
  ]);

  const onHoursToggle = (hours: boolean) => {
    if (hours !== null) {
      if (hours) calendar.setDayView(dateFrom || new Date());
      else {
        const date = new Date();

        calendar.setMonthView(date.getFullYear(), date.getMonth());
      }
    }
  };

  return (
    <>
      <UsersField disabled={disableUserField} />
      <HoursToggle
        disabled={disabled}
        hours={isDayView}
        onChange={onHoursToggle}
      />
      <DatesRangeField disabled={disabled} hours={isDayView} />
      {isDayView && <TimeRangeField disabled={disabled} />}
      <TotalDatesOrHours
        dateFrom={dateFrom}
        dateTo={dateTo}
        startTime={startTime}
        endTime={endTime}
        hours={isDayView}
        isHolidayDate={(date) =>
          holidayLookup.isHolidayDate(date, selectedUserBranchId)
        }
      />
      <TypeField disabled={disabled} />
      <NotesField disabled={disabled} />
    </>
  );
}
